import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNotification } from "@mmfestival/shared-components/common/NotificationContext";
import {
  getFirestore,
  collection,
  query,
  getDocs,
  updateDoc,
  doc,
  deleteDoc,
  orderBy,
  limit,
  startAfter,
  getDoc, // Tambahkan ini
} from "firebase/firestore";
import axios from "axios";
import { getAuth } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faEdit,
  faTrash,
  faSearch,
  faEnvelope,
  faDownload,
  faChevronDown,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";
import { InputField, InputFieldProfile } from "@mmfestival/shared-components";

const PAGE_SIZE = 50;

const API_BASE =
  import.meta.env.VITE_API_BASE_URL ||
  (window.location.hostname === "localhost" ? "http://localhost:3000" : "");

if (!API_BASE) {
  throw new Error("VITE_API_BASE_URL is missing");
}

const getAuthHeaders = async () => {
  const auth = getAuth();
  if (!auth.currentUser) throw new Error("Not authenticated");
  const token = await auth.currentUser.getIdToken();
  return { Authorization: `Bearer ${token}` };
};

const SkeletonRow = React.memo(() => (
  <tr className="border-b border-gray-200">
    <td className="px-4 py-4">
      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
    </td>
    <td className="px-4 py-4">
      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
    </td>
    <td className="px-4 py-4">
      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
    </td>
    <td className="px-4 py-4">
      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
    </td>
    <td className="px-4 py-4">
      <div className="h-4 bg-gray-200 rounded animate-pulse w-4"></div>
    </td>
  </tr>
));

export const SectionAdminUser = ({ user }) => {
  const { showNotification, showConfirm } = useNotification();
  const [state, setState] = useState({
    users: [],
    filteredUsers: [],
    searchQuery: "",
    selectedUsers: [],
    loading: true,
    loadingMore: false,
    lastVisible: null,
    hasMore: true,
    totalUsers: 0,
    totalFiltered: 0,
    sortGenderDirection: null,
    allUsers: [],
    emailTemplateId: 3, // Default template ID
    isSendingEmail: false,
  });
  const [formData, setFormData] = useState({
    namaDepan: "",
    namaBelakang: "",
    email: "",
    alamat: "",
    bibName: "",
    gender: "",
    golonganDarah: "",
    hubunganKontakDarurat: "",
    kontakDarurat: "",
    kota: "",
    memberId: "",
    namaKontakDarurat: "",
    negara: "",
    nomorIdentitas: "",
    photoURL: "",
    provinsi: "",
    role: "user",
    sizeJersey: "",
    tanggalLahir: "",
    telepon: "",
  });
  const [viewUser, setViewUser] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);

  const db = getFirestore();
  const navigate = useNavigate();
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Validasi format email
  const isValidEmail = (email) => {
    if (!email || typeof email !== "string") {
      return false;
    }
    const trimmedEmail = email.trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
  };

  // Fungsi untuk delay (untuk menghindari rate limit)
  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // Fetch users with pagination
  const loadUsers = useCallback(
    async (lastDoc = null, sortDirection = state.sortGenderDirection) => {
      if (!isMounted.current) return;

      setState((prev) => ({
        ...prev,
        loading: !lastDoc,
        loadingMore: !!lastDoc,
        users: lastDoc ? prev.users : [],
        filteredUsers: state.searchQuery ? prev.filteredUsers : [],
      }));

      try {
        const usersCollection = collection(db, "users");
        const constraints = [orderBy("namaLengkap")];
        if (sortDirection) {
          constraints.unshift(orderBy("gender", sortDirection));
        }
        const baseQuery = lastDoc
          ? query(
              usersCollection,
              ...constraints,
              startAfter(lastDoc),
              limit(PAGE_SIZE)
            )
          : query(usersCollection, ...constraints, limit(PAGE_SIZE));

        const usersSnapshot = await getDocs(baseQuery);
        const usersList = usersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          sentTemplateIds: doc.data().sentTemplateIds || [], // Pastikan sentTemplateIds selalu array
        }));

        if (!isMounted.current) return;

        setState((prev) => {
          const updatedUsers = lastDoc
            ? [...prev.users, ...usersList]
            : usersList;
          return {
            ...prev,
            users: updatedUsers,
            filteredUsers: state.searchQuery
              ? prev.filteredUsers
              : updatedUsers,
            lastVisible:
              usersSnapshot.docs[usersSnapshot.docs.length - 1] || null,
            loading: false,
            loadingMore: false,
            hasMore: usersSnapshot.docs.length === PAGE_SIZE,
            totalFiltered: state.searchQuery
              ? prev.totalFiltered
              : prev.totalUsers,
          };
        });
      } catch (err) {
        console.error("Error fetching users:", err);
        if (isMounted.current) {
          setState((prev) => ({
            ...prev,
            loading: false,
            loadingMore: false,
            error: "Gagal memuat data pengguna. Silakan coba lagi.",
          }));
          showNotification(
            "error",
            "Gagal memuat data pengguna. Silakan coba lagi."
          );
        }
      }
    },
    [state.searchQuery, state.sortGenderDirection, showNotification]
  );

  // Fetch total users count
  const fetchTotalUsers = useCallback(async () => {
    try {
      const usersCollection = collection(db, "users");
      const snapshot = await getDocs(usersCollection);
      if (isMounted.current) {
        setState((prev) => ({
          ...prev,
          totalUsers: snapshot.size,
          totalFiltered: state.searchQuery ? prev.totalFiltered : snapshot.size,
        }));
      }
    } catch (err) {
      console.error("Error fetching total users:", err);
      if (isMounted.current) {
        showNotification(
          "error",
          "Gagal memuat jumlah pengguna. Silakan coba lagi."
        );
      }
    }
  }, [state.searchQuery, showNotification]);

  // Fetch all users for search filtering
  const fetchAllUsers = useCallback(async () => {
    try {
      const usersCollection = collection(db, "users");
      const snapshot = await getDocs(usersCollection);
      const allUsers = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        sentTemplateIds: doc.data().sentTemplateIds || [], // Pastikan sentTemplateIds selalu array
      }));
      if (isMounted.current) {
        setState((prev) => ({
          ...prev,
          allUsers,
          totalUsers: snapshot.size,
          totalFiltered: state.searchQuery ? prev.totalFiltered : snapshot.size,
        }));
      }
    } catch (err) {
      console.error("Error fetching all users:", err);
      if (isMounted.current) {
        showNotification(
          "error",
          "Gagal memuat semua data pengguna. Silakan coba lagi."
        );
      }
    }
  }, [state.searchQuery, showNotification]);

  // Handle search
  useEffect(() => {
    if (state.searchQuery) {
      const filtered = (state.allUsers || state.users).filter((u) =>
        [
          u.namaLengkap,
          u.name,
          u.email,
          u.memberId,
          u.sentTemplateIds?.join(", "), // Sertakan sentTemplateIds dalam pencarian
        ]
          .filter(Boolean)
          .some((field) =>
            field
              .toString()
              .toLowerCase()
              .includes(state.searchQuery.toLowerCase())
          )
      );
      setState((prev) => ({
        ...prev,
        filteredUsers: filtered.slice(0, PAGE_SIZE),
        fullFilteredList: filtered,
        totalFiltered: filtered.length,
        hasMore: filtered.length > PAGE_SIZE,
        lastVisible: null,
      }));
    } else {
      setState((prev) => ({
        ...prev,
        filteredUsers: prev.users,
        fullFilteredList: [],
        totalFiltered: prev.totalUsers,
        hasMore: prev.users.length < prev.totalUsers,
      }));
    }
  }, [state.searchQuery, state.users, state.allUsers, state.totalUsers]);

  // Initial data fetch
  useEffect(() => {
    loadUsers();
    fetchTotalUsers();
    fetchAllUsers();
  }, [loadUsers, fetchTotalUsers, fetchAllUsers]);

  // Load more users
  const loadMore = useCallback(() => {
    if (state.loading || state.loadingMore || !state.hasMore) return;

    if (state.searchQuery) {
      const currentIndex = Math.floor(state.filteredUsers.length / PAGE_SIZE);
      const startIndex = currentIndex * PAGE_SIZE;
      const additionalUsers = state.fullFilteredList.slice(
        startIndex,
        startIndex + PAGE_SIZE
      );
      if (!additionalUsers.length) {
        setState((prev) => ({ ...prev, hasMore: false }));
        return;
      }
      setState((prev) => ({
        ...prev,
        filteredUsers: [...prev.filteredUsers, ...additionalUsers],
        hasMore: startIndex + PAGE_SIZE < prev.fullFilteredList.length,
      }));
    } else {
      loadUsers(state.lastVisible);
    }
  }, [
    state.loading,
    state.loadingMore,
    state.hasMore,
    state.searchQuery,
    state.filteredUsers,
    state.fullFilteredList,
    state.lastVisible,
    loadUsers,
  ]);

  // Handle gender sort toggle
  const handleGenderSortToggle = useCallback(() => {
    const newDirection = state.sortGenderDirection === "desc" ? "asc" : "desc";
    setState((prev) => ({
      ...prev,
      sortGenderDirection: newDirection,
      lastVisible: null,
      hasMore: true,
      users: [],
      filteredUsers: [],
    }));
    loadUsers(null, newDirection);
  }, [state.sortGenderDirection, loadUsers]);

  // Select users
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setState((prev) => ({
        ...prev,
        selectedUsers: prev.filteredUsers.map((u) => u.id),
      }));
    } else {
      setState((prev) => ({ ...prev, selectedUsers: [] }));
    }
  };

  const handleSelectUser = (userId) => {
    setState((prev) => ({
      ...prev,
      selectedUsers: prev.selectedUsers.includes(userId)
        ? prev.selectedUsers.filter((id) => id !== userId)
        : [...prev.selectedUsers, userId],
    }));
  };

  // Export to CSV
  const handleExportCSV = () => {
    if (state.selectedUsers.length === 0) {
      showNotification(
        "error",
        "Pilih setidaknya satu pengguna untuk diekspor."
      );
      return;
    }

    const selectedData = state.users.filter((u) =>
      state.selectedUsers.includes(u.id)
    );
    const headers = [
      "Nama",
      "Gender",
      "Kota",
      "Alamat",
      "Provinsi",
      "Negara",
      "Telepon",
      "Nomor Identitas",
      "Tanggal Lahir",
      "Golongan Darah",
      "Kontak Darurat",
      "Nama Kontak Darurat",
      "Hubungan Kontak Darurat",
      "Bib Name",
      "Size Jersey",
      "Email",
      "Member ID",
      "Role",
      "Template Email Terkirim",
    ];

    const rows = selectedData.map((u) => [
      u.namaLengkap || u.name || "-",
      u.gender || "-",
      u.kota || "-",
      u.alamat || "-",
      u.provinsi || "-",
      u.negara || "-",
      u.telepon || "-",
      u.nomorIdentitas || "-",
      u.tanggalLahir || "-",
      u.golonganDarah || "-",
      u.kontakDarurat || "-",
      u.namaKontakDarurat || "-",
      u.hubunganKontakDarurat || "-",
      u.bibName || "-",
      u.sizeJersey || "-",
      u.email || "-",
      u.memberId || "-",
      u.role || "user",
      u.sentTemplateIds?.length > 0 ? u.sentTemplateIds.join(", ") : "-",
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        row
          .map((field) =>
            typeof field === "string" && field.includes(",")
              ? `"${field}"`
              : field
          )
          .join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `users_export_${
      new Date().toISOString().split("T")[0]
    }.csv`;
    link.click();
    URL.revokeObjectURL(link.href);

    showNotification("success", "Data pengguna berhasil diekspor ke CSV.");
  };

  // Handle form changes
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setState((prev) => ({ ...prev, loading: true }));
    try {
      if (!formData.namaDepan || !formData.namaBelakang) {
        showNotification(
          "error",
          "Nama depan dan nama belakang tidak boleh kosong!"
        );
        setState((prev) => ({ ...prev, loading: false }));
        return;
      }

      const namaLengkap =
        `${formData.namaDepan} ${formData.namaBelakang}`.trim();
      const userRef = doc(db, "users", editUser.id);
      await updateDoc(userRef, {
        ...formData,
        namaLengkap,
        name: namaLengkap,
        tanggalLahir: formData.tanggalLahir || null,
        updatedAt: new Date(),
      });
      setState((prev) => ({
        ...prev,
        users: prev.users.map((u) =>
          u.id === editUser.id
            ? { id: editUser.id, ...formData, namaLengkap, name: namaLengkap }
            : u
        ),
        filteredUsers: prev.filteredUsers.map((u) =>
          u.id === editUser.id
            ? { id: editUser.id, ...formData, namaLengkap, name: namaLengkap }
            : u
        ),
        allUsers: prev.allUsers.map((u) =>
          u.id === editUser.id
            ? { id: editUser.id, ...formData, namaLengkap, name: namaLengkap }
            : u
        ),
      }));
      setEditUser(null);
      setFormData({
        namaDepan: "",
        namaBelakang: "",
        email: "",
        alamat: "",
        bibName: "",
        gender: "",
        golonganDarah: "",
        hubunganKontakDarurat: "",
        kontakDarurat: "",
        kota: "",
        memberId: "",
        namaKontakDarurat: "",
        negara: "",
        nomorIdentitas: "",
        photoURL: "",
        provinsi: "",
        role: "user",
        sizeJersey: "",
        tanggalLahir: "",
        telepon: "",
      });
      showNotification("success", "Data pengguna berhasil diperbarui.");
    } catch (err) {
      console.error("Error updating user:", err);
      showNotification(
        "error",
        "Gagal memperbarui data pengguna. Silakan coba lagi."
      );
    } finally {
      setState((prev) => ({ ...prev, loading: false }));
    }
  };

  // Handle user actions
  const handleEdit = (user) => {
    const fullName = user.namaLengkap || user.name || "";
    const [namaDepan, ...namaBelakang] = fullName.split(" ");
    setEditUser({
      id: user.id,
      ...user,
      namaDepan: namaDepan || "",
      namaBelakang: namaBelakang.join(" ") || "",
    });
    setFormData({
      namaDepan: namaDepan || "",
      namaBelakang: namaBelakang.join(" ") || "",
      email: user.email || "",
      alamat: user.alamat || "",
      bibName: user.bibName || "",
      gender: user.gender || "",
      golonganDarah: user.golonganDarah || "",
      hubunganKontakDarurat: user.hubunganKontakDarurat || "",
      kontakDarurat: user.kontakDarurat || "",
      kota: user.kota || "",
      memberId: user.memberId || "",
      namaKontakDarurat: user.namaKontakDarurat || "",
      negara: user.negara || "",
      nomorIdentitas: user.nomorIdentitas || "",
      photoURL: user.photoURL || "",
      provinsi: user.provinsi || "",
      role: user.role || "user",
      sizeJersey: user.sizeJersey || "",
      tanggalLahir: user.tanggalLahir || "",
      telepon: user.telepon || "",
    });
  };

  const handleView = (user) => {
    setViewUser(user);
  };

  const handleEditCancel = () => {
    setEditUser(null);
    setFormData({
      namaDepan: "",
      namaBelakang: "",
      email: "",
      alamat: "",
      bibName: "",
      gender: "",
      golonganDarah: "",
      hubunganKontakDarurat: "",
      kontakDarurat: "",
      kota: "",
      memberId: "",
      namaKontakDarurat: "",
      negara: "",
      nomorIdentitas: "",
      photoURL: "",
      provinsi: "",
      role: "user",
      sizeJersey: "",
      tanggalLahir: "",
      telepon: "",
    });
  };

  const handleDelete = (id) => {
    if (id === user?.uid) {
      showNotification(
        "error",
        "Anda tidak dapat menghapus akun Anda sendiri."
      );
      return;
    }
    showConfirm(
      "Apakah Anda yakin ingin menghapus pengguna ini?",
      async () => {
        try {
          await deleteDoc(doc(db, "users", id));
          setState((prev) => ({
            ...prev,
            users: prev.users.filter((u) => u.id !== id),
            filteredUsers: prev.filteredUsers.filter((u) => u.id !== id),
            allUsers: prev.allUsers.filter((u) => u.id !== id),
            selectedUsers: prev.selectedUsers.filter(
              (selectedId) => selectedId !== id
            ),
            totalUsers: prev.totalUsers - 1,
            totalFiltered: prev.totalFiltered - 1,
          }));
          showNotification("success", "Pengguna berhasil dihapus.");
        } catch (err) {
          console.error("Error deleting user:", err);
          showNotification("error", "Gagal menghapus pengguna: " + err.message);
        }
      },
      "Hapus",
      "Batal"
    );
  };

  // Kirim email menggunakan Brevo API
  const sendEmail = async (e) => {
    e.preventDefault();
    setState((prev) => ({ ...prev, isSendingEmail: true }));

    try {
      const templateId = state.emailTemplateId || 3;
      const eligibleUsers = state.allUsers.filter(
        (u) =>
          state.selectedUsers.includes(u.id) &&
          isValidEmail(u.email) &&
          (!u.sentTemplateIds ||
            !u.sentTemplateIds.includes(templateId.toString()))
      );

      if (eligibleUsers.length === 0) {
        showNotification(
          "error",
          `Tidak ada pengguna yang dipilih dengan email valid yang belum menerima template ID ${templateId}.`
        );
        setState((prev) => ({ ...prev, isSendingEmail: false }));
        return;
      }

      const recipients = eligibleUsers.map((u) => ({
        id: u.id,
        email: u.email ? u.email.trim() : "",
        name: u.namaLengkap || u.name || "Pengguna",
        isValid: isValidEmail(u.email),
      }));

      let totalSent = 0;
      const errors = [];

      for (const recipient of recipients) {
        try {
          const headers = await getAuthHeaders();
          await axios.post(
            `${API_BASE}/api/send-brevo-email`,
            {
              recipients: [{ email: recipient.email, name: recipient.name }],
              subject: "MMFestival Notification",
              templateId: templateId,
            },
            { headers }
          );

          // Update Firestore untuk recipient
          try {
            const userRef = doc(db, "users", recipient.id);
            const userDoc = await getDoc(userRef);
            const currentTemplateIds = userDoc.data().sentTemplateIds || [];
            await updateDoc(userRef, {
              emailSent: true,
              emailSentAt: new Date(),
              sentTemplateIds: [...currentTemplateIds, templateId.toString()],
            });

            setState((prev) => ({
              ...prev,
              users: prev.users.map((u) =>
                u.id === recipient.id
                  ? {
                      ...u,
                      emailSent: true,
                      emailSentAt: new Date(),
                      sentTemplateIds: [
                        ...(u.sentTemplateIds || []),
                        templateId.toString(),
                      ],
                    }
                  : u
              ),
              filteredUsers: prev.filteredUsers.map((u) =>
                u.id === recipient.id
                  ? {
                      ...u,
                      emailSent: true,
                      emailSentAt: new Date(),
                      sentTemplateIds: [
                        ...(u.sentTemplateIds || []),
                        templateId.toString(),
                      ],
                    }
                  : u
              ),
              allUsers: prev.allUsers.map((u) =>
                u.id === recipient.id
                  ? {
                      ...u,
                      emailSent: true,
                      emailSentAt: new Date(),
                      sentTemplateIds: [
                        ...(u.sentTemplateIds || []),
                        templateId.toString(),
                      ],
                    }
                  : u
              ),
            }));

            totalSent += 1;
            showNotification(
              "info",
              `Berhasil mengirim email ke ${recipient.email}. (${totalSent}/${recipients.length})`
            );

            // Delay 100ms untuk menghindari rate limit
            await delay(100);
          } catch (err) {
            console.error(
              `[ERROR] Failed to update email status for ${recipient.id}:`,
              err
            );
            errors.push(
              `Gagal memperbarui status email untuk ${recipient.email}.`
            );
          }
        } catch (err) {
          console.error(
            `[ERROR] Error sending email to ${recipient.email}:`,
            err
          );
          errors.push(
            `Gagal mengirim email ke ${recipient.email}: ${
              err.message || "Terjadi kesalahan."
            }`
          );
        }
      }

      setIsEmailModalOpen(false);

      if (errors.length === 0) {
        showNotification(
          "success",
          `Email dengan template ID ${templateId} berhasil dikirim ke ${totalSent} penerima.`
        );
      } else {
        showNotification(
          "error",
          `Email dikirim ke ${totalSent} penerima, tetapi ada ${
            errors.length
          } kesalahan: ${errors.join("; ")}`
        );
      }
    } catch (err) {
      console.error("[ERROR] Failed to process email sending:", err);
      showNotification(
        "error",
        `Gagal mengirim email: ${err.message || "Terjadi kesalahan."}`
      );
    } finally {
      setState((prev) => ({ ...prev, isSendingEmail: false }));
    }
  };

  const handleSearchChange = (e) => {
    setState((prev) => ({
      ...prev,
      searchQuery: e.target.value,
      hasMore: true,
    }));
  };

  const rangeText = `${state.filteredUsers.length} dari ${state.totalFiltered} Pengguna`;

  return (
    <div className="mb-8">
      {/* User Controls */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="relative w-full sm:max-w-xl">
          <input
            type="text"
            value={state.searchQuery}
            onChange={handleSearchChange}
            placeholder="Cari nama, email, member ID, atau ID template..."
            className="input w-full p-2 pl-10 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-600 text-slate-800"
          />
          <FontAwesomeIcon
            icon={faSearch}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
          />
        </div>
        <div className="flex gap-4">
          <button
            onClick={() => setIsEmailModalOpen(true)}
            disabled={state.selectedUsers.length === 0 || state.isSendingEmail}
            className={`px-4 py-2 rounded-xl text-sm sm:text-base flex items-center gap-2 ${
              state.selectedUsers.length === 0 || state.isSendingEmail
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-cyan-600 text-white hover:bg-cyan-800"
            }`}
          >
            <FontAwesomeIcon icon={faEnvelope} />
            Kirim Email
          </button>
          <button
            onClick={handleExportCSV}
            disabled={state.selectedUsers.length === 0}
            className={`px-4 py-2 rounded-xl text-sm sm:text-base flex items-center gap-2 ${
              state.selectedUsers.length === 0
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-green-600 text-white hover:bg-green-800"
            }`}
          >
            <FontAwesomeIcon icon={faDownload} />
            Export to CSV
          </button>
        </div>
      </div>

      {/* User Table */}
      <div className="bg-white rounded-xl border border-gray-300 overflow-x-auto mb-8">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <input
                  type="checkbox"
                  checked={
                    state.selectedUsers.length === state.filteredUsers.length &&
                    state.filteredUsers.length > 0
                  }
                  onChange={handleSelectAll}
                  className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                />
              </th>
              <th className="px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Nama
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-cyan-600"
                onClick={handleGenderSortToggle}
                aria-label="Urutkan berdasarkan gender"
              >
                <span className="flex items-center">
                  Gender
                  {state.sortGenderDirection && (
                    <FontAwesomeIcon
                      icon={
                        state.sortGenderDirection === "asc"
                          ? faSortUp
                          : faSortDown
                      }
                      className="ml-1 text-gray-500"
                    />
                  )}
                </span>
              </th>
              <th className="px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Kota
              </th>
              <th className="px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Template Email Terkirim
              </th>
              <th className="px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Aksi
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {state.loading ? (
              <SkeletonRow />
            ) : state.filteredUsers.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-4 text-center text-sm text-gray-600"
                >
                  {state.searchQuery
                    ? "Tidak ada pengguna yang cocok dengan pencarian."
                    : "Belum ada pengguna yang terdaftar."}
                </td>
              </tr>
            ) : (
              state.filteredUsers.map((u) => (
                <tr key={u.id}>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={state.selectedUsers.includes(u.id)}
                      onChange={() => handleSelectUser(u.id)}
                      className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                    />
                  </td>
                  <td
                    className={`px-4 py-4 whitespace-nowrap uppercase text-sm ${
                      u.gender === "Perempuan"
                        ? "text-cyan-600"
                        : "text-gray-600"
                    }`}
                  >
                    {u.namaLengkap || u.name || "-"}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {u.gender || "-"}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {u.kota || "-"}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {u.sentTemplateIds?.length > 0
                      ? u.sentTemplateIds.join(", ")
                      : "-"}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleView(u)}
                      className="text-cyan-600 hover:text-cyan-900 mr-3"
                      disabled={state.loading}
                    >
                      <FontAwesomeIcon icon={faEye} />
                    </button>
                    <button
                      onClick={() => handleEdit(u)}
                      className="text-indigo-600 hover:text-indigo-900 mr-3"
                      disabled={state.loading}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button
                      onClick={() => handleDelete(u.id)}
                      className="text-red-600 hover:text-red-900"
                      disabled={state.loading}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Load More and Count */}
      <div className="flex justify-end items-center px-4 py-2">
        <span className="text-sm text-gray-600 mr-4">{rangeText}</span>
        <button
          onClick={loadMore}
          disabled={state.loading || state.loadingMore || !state.hasMore}
          className={`px-3 py-1 rounded-lg transition-colors duration-200 flex items-center gap-1
            ${
              state.loading || state.loadingMore || !state.hasMore
                ? "bg-gray-200 text-gray-600 cursor-not-allowed"
                : "bg-cyan-600 text-white hover:bg-cyan-700"
            }`}
          aria-label={
            state.hasMore
              ? "Muat lebih banyak pengguna"
              : "Akhir daftar pengguna"
          }
        >
          <span>{state.hasMore ? "Lihat lebih" : "Akhir pengguna"}</span>
          <FontAwesomeIcon icon={faChevronDown} />
        </button>
      </div>

      {/* Modal Detail Pengguna */}
      {viewUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 transition-opacity duration-300">
          <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md md:max-w-2xl max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Detail Pengguna
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
              <p>
                <strong>Nama:</strong>{" "}
                {viewUser.namaLengkap || viewUser.name || "-"}
              </p>
              <p>
                <strong>Email:</strong> {viewUser.email || "-"}
              </p>
              <p>
                <strong>Member ID:</strong> {viewUser.memberId || "-"}
              </p>
              <p>
                <strong>Role:</strong> {viewUser.role || "user"}
              </p>
              <p>
                <strong>Template Email Terkirim:</strong>{" "}
                {viewUser.sentTemplateIds?.length > 0
                  ? viewUser.sentTemplateIds.join(", ")
                  : "-"}
              </p>
              <p>
                <strong>Alamat:</strong> {viewUser.alamat || "-"}
              </p>
              <p>
                <strong>Kota:</strong> {viewUser.kota || "-"}
              </p>
              <p>
                <strong>Provinsi:</strong> {viewUser.provinsi || "-"}
              </p>
              <p>
                <strong>Negara:</strong> {viewUser.negara || "-"}
              </p>
              <p>
                <strong>Telepon:</strong> {viewUser.telepon || "-"}
              </p>
              <p>
                <strong>Nomor Identitas:</strong>{" "}
                {viewUser.nomorIdentitas || "-"}
              </p>
              <p>
                <strong>Tanggal Lahir:</strong> {viewUser.tanggalLahir || "-"}
              </p>
              <p>
                <strong>Gender:</strong> {viewUser.gender || "-"}
              </p>
              <p>
                <strong>Golongan Darah:</strong> {viewUser.golonganDarah || "-"}
              </p>
              <p>
                <strong>Kontak Darurat:</strong> {viewUser.kontakDarurat || "-"}
              </p>
              <p>
                <strong>Nama Kontak Darurat:</strong>{" "}
                {viewUser.namaKontakDarurat || "-"}
              </p>
              <p>
                <strong>Hubungan Kontak Darurat:</strong>{" "}
                {viewUser.hubunganKontakDarurat || "-"}
              </p>
              <p>
                <strong>Bib Name:</strong> {viewUser.bibName || "-"}
              </p>
              <p>
                <strong>Size Jersey:</strong> {viewUser.sizeJersey || "-"}
              </p>
            </div>
            <div className="flex justify-center mt-6">
              <button
                onClick={() => setViewUser(null)}
                className="px-4 py-2 bg-gray-300 text-gray-800 rounded-xl hover:bg-gray-400 transition-colors duration-200 w-full max-w-xs"
                disabled={state.loading}
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Edit Pengguna */}
      {editUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 transition-opacity duration-300">
          <div className="bg-white rounded-xl shadow-lg p-4 w-full max-w-md md:max-w-6xl max-h-[80vh] overflow-y-auto">
            <h3 className="text-xl font-semibold text-gray-800 mb-6">
              Edit Pengguna
            </h3>
            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                <InputFieldProfile
                  formData={formData}
                  handleChange={handleChange}
                  isFormDisabled={state.loading}
                  labelColor="text-slate-800"
                  excludeFields={["namaDepan", "namaBelakang", "role"]}
                />
                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-800 mb-2">
                    Role
                  </label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    className="input w-full p-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-600 disabled:bg-gray-100"
                    disabled={state.loading}
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-center gap-4 mt-4">
                <button
                  type="submit"
                  className="min-w-[140px] px-6 py-2.5 bg-cyan-600 text-white rounded-xl hover:bg-cyan-700 transition-colors duration-200 text-base font-medium"
                  disabled={state.loading}
                >
                  {state.loading ? "Menyimpan..." : "Simpan"}
                </button>
                <button
                  type="button"
                  onClick={handleEditCancel}
                  className="min-w-[140px] px-6 py-2.5 bg-gray-300 text-gray-800 rounded-xl hover:bg-gray-400 transition-colors duration-200 text-base font-medium"
                  disabled={state.loading}
                >
                  Batal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Kirim Email */}
      {isEmailModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl py-6 px-4 max-w-lg w-full mx-4">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Kirim Email ke Pengguna
            </h2>
            <form onSubmit={sendEmail}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">
                  Template ID (Brevo)
                </label>
                <InputField
                  type="number"
                  value={state.emailTemplateId}
                  onChange={(e) =>
                    setState((prev) => ({
                      ...prev,
                      emailTemplateId: parseInt(e.target.value) || 3,
                    }))
                  }
                  placeholder="Masukkan ID template Brevo"
                  inputClassName="input w-full p-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-600 bg-white text-slate-800 text-sm"
                />
              </div>
              <div className="mb-4 text-sm text-gray-600">
                <p>
                  Email akan dikirim ke{" "}
                  {
                    state.allUsers.filter(
                      (u) =>
                        state.selectedUsers.includes(u.id) &&
                        isValidEmail(u.email) &&
                        (!u.sentTemplateIds ||
                          !u.sentTemplateIds.includes(
                            state.emailTemplateId.toString()
                          ))
                    ).length
                  }{" "}
                  pengguna dengan email valid dan belum menerima template ID{" "}
                  {state.emailTemplateId || 3}.
                </p>
              </div>
              <div className="flex justify-end gap-4">
                <button
                  type="button"
                  onClick={() => setIsEmailModalOpen(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-800 rounded-xl hover:bg-gray-400"
                  disabled={state.isSendingEmail}
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className={`px-4 py-2 rounded-xl text-white ${
                    state.isSendingEmail
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-cyan-600 hover:bg-cyan-700"
                  }`}
                  disabled={state.isSendingEmail}
                >
                  {state.isSendingEmail ? "Mengirim..." : "Kirim Email"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
